import React, { useState } from "react";
import { motion } from "framer-motion";

// 12 zodiac glyphs in tropical order starting with Aries
const ZODIAC = [
  { sym: "♈", name: "Aries" }, { sym: "♉", name: "Taurus" }, { sym: "♊", name: "Gemini" },
  { sym: "♋", name: "Cancer" }, { sym: "♌", name: "Leo" }, { sym: "♍", name: "Virgo" },
  { sym: "♎", name: "Libra" }, { sym: "♏", name: "Scorpio" }, { sym: "♐", name: "Sagittarius" },
  { sym: "♑", name: "Capricorn" }, { sym: "♒", name: "Aquarius" }, { sym: "♓", name: "Pisces" },
];

const PLANETS = [
  { sym: "☉", radius: 90,  duration: 25, color: "#f5e29a" },  // Sun
  { sym: "☽", radius: 130, duration: 18, color: "#dad7c1" },  // Moon
  { sym: "☿", radius: 170, duration: 36, color: "#c2bba3" },  // Mercury
  { sym: "♀", radius: 210, duration: 50, color: "#e8b9a3" },  // Venus
  { sym: "♂", radius: 248, duration: 70, color: "#c9694e" },  // Mars
  { sym: "♃", radius: 290, duration: 95, color: "#d4af37" },  // Jupiter
  { sym: "♄", radius: 330, duration: 120, color: "#a78b5e" }, // Saturn
];

function Star({ x, y, size, delay }) {
  return (
    <circle cx={x} cy={y} r={size} fill="#fff" opacity={0.6}
      style={{ animation: `twinkle 3s ease-in-out ${delay}s infinite` }} />
  );
}

const seededStars = Array.from({ length: 110 }).map((_, i) => {
  // deterministic pseudo-random
  const s = Math.sin(i * 999.31) * 10000;
  const r = s - Math.floor(s);
  const s2 = Math.sin((i + 7) * 1331.7) * 10000;
  const r2 = s2 - Math.floor(s2);
  return {
    x: r * 1200,
    y: r2 * 800,
    size: 0.4 + (r * 1.6),
    delay: r2 * 4,
  };
});

export default function CosmicHero({ onCTAClick }) {
  const [hoverSessions, setHoverSessions] = useState(false);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
    >
      {/* Background nebula */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(75,0,130,0.32),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_75%_80%,rgba(212,175,55,0.10),transparent_55%)]" />
      </div>

      {/* Twinkling starfield */}
      <svg className="absolute inset-0 w-full h-full -z-10 pointer-events-none" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
        {seededStars.map((s, i) => <Star key={i} {...s} />)}
      </svg>

      {/* Cosmic wheel */}
      <div className="absolute inset-0 flex items-center justify-center -z-0 pointer-events-none">
        <div className="relative" style={{ width: 800, height: 800, maxWidth: "92vw", maxHeight: "92vw" }}>
          {/* Outer zodiac ring (slow spin) */}
          <div className="absolute inset-0 animate-spin-slow">
            <svg viewBox="-400 -400 800 800" className="w-full h-full" data-testid="zodiac-wheel">
              <defs>
                <radialGradient id="ringGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="80%" stopColor="rgba(212,175,55,0)" />
                  <stop offset="100%" stopColor="rgba(212,175,55,0.18)" />
                </radialGradient>
              </defs>
              <circle r="380" fill="none" stroke="rgba(212,175,55,0.18)" strokeWidth="1" />
              <circle r="350" fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="1" />
              {/* 12 division lines */}
              {ZODIAC.map((_, i) => {
                const a = (i * 30 * Math.PI) / 180;
                const x1 = 350 * Math.cos(a), y1 = 350 * Math.sin(a);
                const x2 = 380 * Math.cos(a), y2 = 380 * Math.sin(a);
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(212,175,55,0.4)" strokeWidth="1" />;
              })}
              {/* Zodiac glyphs */}
              {ZODIAC.map((z, i) => {
                const a = ((i * 30 + 15) * Math.PI) / 180;
                const x = 365 * Math.cos(a);
                const y = 365 * Math.sin(a);
                return (
                  <text key={i} x={x} y={y} fill="#D4AF37" fontSize="22"
                    fontFamily="Cormorant Garamond, serif"
                    textAnchor="middle" dominantBaseline="central"
                    transform={`rotate(${(i * 30 + 15 + 90)} ${x} ${y})`}>
                    {z.sym}
                  </text>
                );
              })}
            </svg>
          </div>

          {/* Mid ring (counter spin) */}
          <div className="absolute inset-0 animate-spin-rev">
            <svg viewBox="-400 -400 800 800" className="w-full h-full">
              <circle r="220" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="2 6" />
              <circle r="180" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              {/* Sacred geometry — hexagram */}
              {[0, 60, 120, 180, 240, 300].map((deg, i) => {
                const a = (deg * Math.PI) / 180;
                const x = 200 * Math.cos(a), y = 200 * Math.sin(a);
                return <circle key={i} cx={x} cy={y} r="3" fill="#D4AF37" opacity="0.5" />;
              })}
            </svg>
          </div>

          {/* Orbiting planets */}
          {PLANETS.map((p, i) => (
            <div key={i} className="absolute inset-0 flex items-center justify-center">
              <motion.div
                className="absolute"
                style={{ width: p.radius * 2, height: p.radius * 2 }}
                animate={{ rotate: 360 }}
                transition={{ duration: p.duration, repeat: Infinity, ease: "linear" }}
              >
                <div
                  className="absolute"
                  style={{
                    left: "50%", top: 0, transform: "translateX(-50%)",
                    color: p.color,
                    fontSize: 18,
                    textShadow: `0 0 12px ${p.color}`,
                    fontFamily: "Cormorant Garamond, serif",
                  }}
                >
                  {p.sym}
                </div>
              </motion.div>
            </div>
          ))}

          {/* Center seed */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-[#D4AF37] shadow-[0_0_30px_rgba(212,175,55,0.7)]" />
          </div>
        </div>
      </div>

      {/* Hero content */}
      <div className="relative z-10 text-center px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <p className="font-sans text-xs sm:text-sm tracking-overline text-[#D4AF37] mb-6"
             data-testid="hero-overline">
            self-discovery · healing · transformation
          </p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight text-white leading-[0.95] grid">
            <span
              className={`col-start-1 row-start-1 transition-opacity duration-300 ${
                hoverSessions ? "opacity-0" : "opacity-100"
              }`}
            >
              Awaken to the sacred blueprint
              <br />
              of your <span className="italic gold-text">soul</span>.
            </span>
            <span
              className={`col-start-1 row-start-1 transition-opacity duration-300 ${
                hoverSessions ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={!hoverSessions}
            >
              Access the timeless wisdom
              <br />
              of your <span className="italic gold-text">soul</span>
            </span>
          </h1>
          <div className="mt-8 max-w-2xl mx-auto grid">
            <p
              className={`col-start-1 row-start-1 font-sans text-base sm:text-lg text-slate-300 leading-relaxed transition-opacity duration-300 ${
                hoverSessions ? "opacity-0" : "opacity-100"
              }`}
            >
              One sacred process. Three ancient systems woven together. A clear, electrifying portrait of who you are,
              how you operate, and exactly where to direct your attention to live your full potential.
            </p>
            <p
              className={`col-start-1 row-start-1 font-sans text-base sm:text-lg text-slate-300 leading-relaxed transition-opacity duration-300 ${
                hoverSessions ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={!hoverSessions}
            >
              Gain clarity, insight and healing through soulful exploration and deep regression therapy.
            </p>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="hero-cta-generate"
              onClick={onCTAClick}
              className="btn-ghost-gold rounded-full px-10 py-4 text-sm"
            >
              Reveal my blueprint
            </button>
            <a
              data-testid="hero-cta-sessions"
              href="#sessions"
              onMouseEnter={() => setHoverSessions(true)}
              onMouseLeave={() => setHoverSessions(false)}
              onFocus={() => setHoverSessions(true)}
              onBlur={() => setHoverSessions(false)}
              className="btn-ghost-gold rounded-full px-10 py-4 text-sm inline-flex items-center justify-center"
            >
              Private sessions
            </a>
          </div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#D4AF37]/60 text-xs tracking-overline">
        scroll
      </div>
    </section>
  );
}
